import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import {
  GeneralWidgetPageProps, IframeEventMessage, WidgetRegionData, WidgetResortData, WidgetType,
} from 'components/widget/types';
import { fetchRegionForWidget, fetchResortsForWidget } from 'api/resort/static';
import {
  defineResortsStatuses, defineWidgetType, generateIdsArray, generateTemplateData,
} from 'components/widget/utils';
import { ResortQueryOpenStatus } from 'components/widget/constants';
import { assertShouldBeNot404 } from 'util/wrapProps';
import { dynamicFallback } from '.';

const DynamicSingleResortView = dynamic(() => import('components/widget/views/SingleResortView'), dynamicFallback);
const DynamicMultiResortsView = dynamic(() => import('components/widget/views/MultiResortsView'), dynamicFallback);
const DynamicRegionView = dynamic(() => import('components/widget/views/RegionView'), dynamicFallback);

export type DynamicFetchData = {
  regionData?: WidgetRegionData,
  resorts?: WidgetResortData[],
  uuidList?: string
  widgetType?: WidgetType
  resortsStatuses?: ResortQueryOpenStatus
};

export type WidgetPageProps = {
  isWidgetPage: boolean
} & GeneralWidgetPageProps & DynamicFetchData;

function WidgetFrame({
  widgetType, resorts, uuidList, resortsStatuses, regionData,
}: WidgetPageProps) {
  useEffect(() => {
    const message: IframeEventMessage = `${document.body.scrollHeight}px`;
    window.parent.postMessage(message, '*');
  }, []);

  if (widgetType === WidgetType.SINGLE_RESORT && resorts?.[0]) {
    return <DynamicSingleResortView data={resorts[0]} />;
  }

  if (widgetType === WidgetType.LIST_OF_RESORTS && uuidList) {
    return <DynamicMultiResortsView resorts={resorts} uuids={uuidList} resortsStatuses="" />;
  }

  if (widgetType === WidgetType.SINGLE_REGION && regionData && resortsStatuses !== undefined) {
    return <DynamicRegionView data={regionData} resortsStatuses={resortsStatuses} />;
  }

  return null;
}

export default WidgetFrame;

export const fetchProps = async ({ query }: GetServerSidePropsContext<{}>): Promise<WidgetPageProps> => {
  const data: DynamicFetchData = {};
  const widgetType = defineWidgetType(query);
  const uuidList = widgetType ? generateIdsArray(query[widgetType]) : '';
  data.uuidList = uuidList;

  if (widgetType === WidgetType.SINGLE_REGION) {
    const resortsStatuses = defineResortsStatuses(query);
    data.resortsStatuses = resortsStatuses;
    data.regionData = await assertShouldBeNot404(() => fetchRegionForWidget(uuidList, resortsStatuses));
  } else {
    data.resorts = await assertShouldBeNot404(() => fetchResortsForWidget(uuidList));
  }

  return {
    ...data,
    ...generateTemplateData(widgetType),
    widgetType,
    isWidgetPage: true,
    type: 'global',
    route: 'widget',
  };
};

export const getWidgetServerSideProps: GetServerSideProps<WidgetPageProps> = async (context: GetServerSidePropsContext<{}>) => {
  const f: GetServerSidePropsResult<WidgetPageProps> = { props: await fetchProps(context) };

  return f;
};
